import { Button } from "@/components/ui/baseUi/button"
import { word } from "@/modules/core/utils/i18n"
import { CheckoutSummary } from "@/modules/payment/components/checkoutSummary"
import { ProgramInfoHeader } from "@/modules/programs/components/programInfoHeader"
import type { UserProgramDetailsEntity } from "@/modules/programs/entities/userProgramDetailsEntity"
import { PaymentElement, useCheckout } from "@stripe/react-stripe-js/checkout"
import type { StripeError } from "@stripe/stripe-js"
import { useState } from "react"

type CheckoutFormProps = {
  program?: UserProgramDetailsEntity
  className?: string
  onCompleteCheckout?: () => void
} & React.ComponentProps<"div">

export function CustomCheckoutForm({ program, className, onCompleteCheckout, ...props }: CheckoutFormProps) {
  const checkoutState = useCheckout()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const defaultClassName = "flex flex-col grow-1 gap-6"

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setErrorMessage(null)

    if (checkoutState.type === "loading") {
      return
    }

    if (checkoutState.type === "error") {
      setErrorMessage(checkoutState.error.message)
      return
    }

    setIsSubmitting(true)

    const { checkout } = checkoutState
    checkout
      .confirm()
      .then((result) => {
        if (result.type === "error") {
          setErrorMessage(result.error.message)
        } else {
          onCompleteCheckout?.()
        }
      })
      .catch((error: StripeError) => {
        if (error.message) {
          setErrorMessage(error.message)
        } else {
          setErrorMessage(word("global.error.label"))
        }
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const isLoading = checkoutState.type === "loading"

  return (
    <div className={className ?? defaultClassName} {...props}>
      {program && <ProgramInfoHeader programId={program.id} />}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
        <CheckoutSummary />

        <PaymentElement />

        {errorMessage && <p className="text-sm text-textError">{errorMessage}</p>}

        <Button type="submit" variant="primary" size="lg" disabled={isLoading || isSubmitting} className="w-full">
          {isSubmitting ? word("global.loading") : word("program.subscription.paying.proceed.title")}
        </Button>
      </form>
    </div>
  )
}
